import { useState } from "react";
import emailjs from "@emailjs/browser";
import { Link } from "react-router-dom";
import { SITE, SERVICES } from "../data/siteData";
import glolexLogo from "../assests/images/glolex_logo.jpeg";

// ─── EmailJS config ──────────────────────────────────────────────────────────
const EMAILJS_SERVICE_ID = "service_88cnorj";
const EMAILJS_TEMPLATE_ID = "template_n2ceplr";
const EMAILJS_PUBLIC_KEY = "QhNX65dx4FLgaiT7s";

// ─── Email validation ─────────────────────────────────────────────────────────
const isValidEmail = (value) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

const Footer = () => {
  const currentYear = new Date().getFullYear();

  // Newsletter state
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [validationMsg, setValidationMsg] = useState("");

  const handleSubscribe = async (e) => {
    e.preventDefault();

    // Reset messages
    setSuccessMessage("");
    setErrorMessage("");
    setValidationMsg("");

    // Validate
    if (!email.trim()) {
      setValidationMsg("Please enter a valid email address.");
      return;
    }
    if (!isValidEmail(email)) {
      setValidationMsg("Please enter a valid email address.");
      return;
    }

    setLoading(true);

    try {
      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        {
          subscriber_email: email.trim(),
          subscription_date: new Date().toLocaleString(),
        },
        EMAILJS_PUBLIC_KEY
      );

      setEmail("");
      setSuccessMessage(
        "✅ You're subscribed. Watch your inbox for the Glolex Trade Intelligence Bulletin — FTA updates, Trade alerts and compliance insights, delivered monthly."
      );
    } catch (err) {
      setErrorMessage("Subscription failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const companyLinks = [
    { label: "About Us", path: "/about" },
    { label: "Our Team", path: "/about#team" },
    /* TEMPORARILY HIDDEN: { label: "Case Studies", path: "/case-studies" }, */
    { label: "Insights", path: "/insights" },
    { label: "Industries", path: "/industries" },
    { label: "Careers", path: "/contact" },
    { label: "Privacy Policy", path: "#" },
    { label: "Terms of Use", path: "#" },
  ];

  const serviceLinks = SERVICES.map((s) => ({ label: s.title, path: `/services/${s.slug}` }));

  return (
    <footer id="footer" style={{ background: "#070C14" }}>
      {/* ─── Main Footer ─── */}
      <div className="container-fluid px-4 px-lg-5" style={{ paddingTop: "64px", paddingBottom: "48px" }}>
        <div className="row g-5">

          {/* Column 1: Brand */}
          <div className="col-12 col-md-6 col-lg-4">
            <Link to="/" className="navbar-brand d-inline-block mb-4" aria-label="GLOLEX Business Solutions Home" style={{ textDecoration: "none", padding: 0 }}>
              <img
                src={glolexLogo}
                alt="Glolex Business Solutions"
                style={{ width: "auto", maxWidth: "240px", maxHeight: "56px", objectFit: "contain", display: "block", transform: "scale(1.20)", transformOrigin: "left center" }}
              />
            </Link>

            <p style={{ color: "#64748b", fontSize: "13px", lineHeight: 1.7, marginBottom: "8px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em" }}>
              Simplifying Global Trade
            </p>
            <p style={{ color: "#64748b", fontSize: "12px", lineHeight: 1.7, marginBottom: "16px" }}>
              Customs | Trade Compliance | Supply Chain
            </p>
            <p style={{ color: "#475569", fontSize: "13px", lineHeight: 1.8, marginBottom: "24px" }}>
              A specialist customs, trade compliance, duty optimisation, EPR, DGFT, and government licensing advisory firm.
            </p>

            {/* Social Icons */}
            <div className="d-flex gap-2">
              {[
                {
                  href: SITE.social.linkedin, label: "LinkedIn",
                  icon: <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />,
                },
                {
                  href: SITE.social.twitter, label: "Twitter / X",
                  icon: <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.73-8.835L1.254 2.25H8.08l4.253 5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z" />,
                },
                {
                  href: SITE.social.youtube, label: "YouTube",
                  icon: <path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />,
                },
                {
                  href: SITE.social.whatsapp, label: "WhatsApp",
                  hoverColor: "#25D366",
                  icon: <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />,
                },
              ].map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={social.label}
                  style={{
                    width: "34px", height: "34px", borderRadius: "8px",
                    background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    color: "#64748b", transition: "all .2s",
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = social.hoverColor || "#2BAABC"; e.currentTarget.style.color = "#fff"; e.currentTarget.style.borderColor = social.hoverColor || "#2BAABC"; }}
                  onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,0.05)"; e.currentTarget.style.color = "#64748b"; e.currentTarget.style.borderColor = "rgba(255,255,255,0.08)"; }}
                >
                  <svg style={{ width: "15px", height: "15px" }} fill="currentColor" viewBox="0 0 24 24">
                    {social.icon}
                  </svg>
                </a>
              ))}
            </div>
          </div>

          {/* Column 2: Services */}
          <div className="col-12 col-md-6 col-lg-3">
            <h3 style={{ color: "#fff", fontSize: "12px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: "20px" }}>
              Services
            </h3>
            <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
              {serviceLinks.map((link) => (
                <li key={link.path} style={{ marginBottom: "10px" }}>
                  <Link
                    to={link.path}
                    style={{ color: "#64748b", fontSize: "13px", textDecoration: "none", transition: "color .2s" }}
                    onMouseEnter={e => e.currentTarget.style.color = "#2BAABC"}
                    onMouseLeave={e => e.currentTarget.style.color = "#64748b"}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 3: Company */}
          <div className="col-12 col-md-6 col-lg-2">
            <h3 style={{ color: "#fff", fontSize: "12px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: "20px" }}>
              Company
            </h3>
            <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
              {companyLinks.map((link) => (
                <li key={link.label} style={{ marginBottom: "10px" }}>
                  <Link
                    to={link.path}
                    style={{ color: "#64748b", fontSize: "13px", textDecoration: "none", transition: "color .2s" }}
                    onMouseEnter={e => e.currentTarget.style.color = "#2BAABC"}
                    onMouseLeave={e => e.currentTarget.style.color = "#64748b"}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 4: Contact & Newsletter */}
          <div className="col-12 col-md-6 col-lg-3">
            <h3 style={{ color: "#fff", fontSize: "12px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: "20px" }}>
              Contact
            </h3>
            <div style={{ marginBottom: "16px" }}>
              <a
                href={`mailto:${SITE.email}`}
                style={{ color: "#2BAABC", fontSize: "13px", textDecoration: "none" }}
              >
                {SITE.email}
              </a>
            </div>
            <div style={{ marginBottom: "24px" }}>
              <a
                href={`tel:${SITE.phone}`}
                style={{ color: "#64748b", fontSize: "13px", textDecoration: "none" }}
              >
                {SITE.phone}
              </a>
            </div>

            {/* Newsletter */}
            <h4 style={{ color: "#fff", fontSize: "12px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: "12px" }}>
              Trade Intelligence Bulletin
            </h4>
            <p style={{ color: "#475569", fontSize: "12px", lineHeight: 1.6, marginBottom: "14px" }}>
              Subscribe for FTA updates, QCO alerts and trade compliance insights.
            </p>
            <form onSubmit={handleSubscribe} noValidate style={{ display: "flex", gap: "8px" }}>
              <input
                id="newsletter-email"
                type="email"
                placeholder="Your email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setValidationMsg("");
                  setErrorMessage("");
                }}
                disabled={loading}
                aria-label="Email address for Trade Intelligence Bulletin"
                style={{
                  flex: 1, padding: "9px 12px",
                  background: "rgba(255,255,255,0.06)",
                  border: `1px solid ${validationMsg ? "#f87171" : "rgba(255,255,255,0.12)"}`,
                  borderRadius: "6px", color: "#fff",
                  fontSize: "13px", outline: "none",
                  opacity: loading ? 0.6 : 1,
                  transition: "border-color .2s, opacity .2s",
                }}
              />
              <button
                id="newsletter-subscribe-btn"
                type="submit"
                disabled={loading}
                style={{
                  padding: "9px 16px",
                  background: loading ? "#1d8fa0" : "#2BAABC",
                  border: "none",
                  borderRadius: "6px",
                  color: "#fff",
                  fontWeight: 600,
                  fontSize: "12px",
                  cursor: loading ? "not-allowed" : "pointer",
                  transition: "background .2s",
                  whiteSpace: "nowrap",
                  opacity: loading ? 0.8 : 1,
                }}
              >
                {loading ? "Subscribing..." : "Subscribe"}
              </button>
            </form>

            {/* Validation message */}
            {validationMsg && (
              <p style={{ color: "#f87171", fontSize: "11.5px", marginTop: "8px", marginBottom: 0, lineHeight: 1.5 }}>
                {validationMsg}
              </p>
            )}

            {/* Success message */}
            {successMessage && (
              <p style={{ color: "#4ade80", fontSize: "11.5px", marginTop: "10px", marginBottom: 0, lineHeight: 1.6 }}>
                {successMessage}
              </p>
            )}

            {/* Error message */}
            {errorMessage && (
              <p style={{ color: "#f87171", fontSize: "11.5px", marginTop: "8px", marginBottom: 0, lineHeight: 1.5 }}>
                {errorMessage}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* ─── Bottom Bar ─── */}
      <div style={{ borderTop: "1px solid rgba(255,255,255,0.05)" }}>
        <div className="container-fluid px-4 px-lg-5 py-4">
          <div className="row align-items-center g-3">
            <div className="col-12 col-md-8">
              <p style={{ color: "#374151", fontSize: "12px", margin: 0 }}>
                © {currentYear} Glolex Business Solutions. All rights reserved. | Customs · Trade Compliance · Supply Chain · India
              </p>
            </div>
            <div className="col-12 col-md-4 text-md-end">
              <p style={{ color: "#374151", fontSize: "11px", margin: 0, lineHeight: 1.5 }}>
                Content on this website is for informational purposes only and does not constitute legal or professional advice.
              </p>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
